import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { validatePalletGeometry, geometryErrorMessage, palletAxisLimit } from "../lib/palletGeometry";
import { isLargeLoadingPlan, loadingUnitLabel, loadingUnitName, loadingUnitPlural } from "../lib/loadingUnits";
import PageHeader from "@/components/PageHeader";
import DocumentDateEditor from "../components/DocumentDateEditor";
import PalletSharing from "../components/PalletSharing";
import Pallet3D from "@/components/Pallet3D";
import LayerPanel from "@/components/LayerPanel";
import NestedPalletEditor from "../components/NestedPalletEditor";
import { toast } from "sonner";
import {
  FilePdf, FileXls, CheckCircle, LockKey, LockOpen,
  ArrowsClockwise, ArrowsHorizontal, ArrowsVertical, ArrowUp, ArrowDown,
  ArrowLeft, ArrowRight, ArrowsOutCardinal, Trash, ArrowFatUp, ArrowFatDown,
  QrCode, Receipt, ArrowUUpLeft, Plus, Package,
} from "@phosphor-icons/react";

const STEP = 5; // cm nudge amount
export const EPS = 1e-6;

// ---------- Helpers for box manipulation ----------
export function overlapsExisting(pallet, x, y, z, dx, dy, dz, skipIdx) {
  for (let i = 0; i < pallet.boxes.length; i++) {
    if (i === skipIdx) continue;
    const b = pallet.boxes[i];
    if (
      x < b.x + b.dx - EPS && x + dx > b.x + EPS &&
      y < b.y + b.dy - EPS && y + dy > b.y + EPS &&
      z < b.z + b.dz - EPS && z + dz > b.z + EPS
    ) return true;
  }
  return false;
}

export function fitsInPallet(pallet, x, y, z, dx, dy, dz, L, W, H) {
  const maxX = palletAxisLimit(pallet, "x", L);
  const maxY = palletAxisLimit(pallet, "y", W);
  const maxZ = palletAxisLimit(pallet, "z", H);
  return (
    x >= -EPS && y >= -EPS && z >= -EPS &&
    x + dx <= maxX + EPS && y + dy <= maxY + EPS && z + dz <= maxZ + EPS
  );
}

export function findGravityZBelow(pallet, x, y, currentZ, dx, dy, skipIdx = -1) {
  let restingZ = 0;
  for (let i = 0; i < pallet.boxes.length; i++) {
    if (i === skipIdx) continue;
    const b = pallet.boxes[i];
    const overlapX = Math.min(x + dx, b.x + b.dx) - Math.max(x, b.x);
    const overlapY = Math.min(y + dy, b.y + b.dy) - Math.max(y, b.y);
    if (overlapX <= EPS || overlapY <= EPS) continue;
    if (b.can_support_load === false) continue;
    const supportTop = b.z + b.dz;
    if (supportTop <= currentZ + EPS) restingZ = Math.max(restingZ, supportTop);
  }
  return Number(restingZ.toFixed(2));
}

export function findRestingZ(pallet, x, y, dx, dy, dz, skipIdx = -1) {
  // Choose the LOWEST collision-free supported level. This is the key difference
  // from the old "highest overlapping top" logic: an elevated box no longer forces
  // another box to jump above it when there is still free room on the pallet floor.
  const levels = new Set([0]);
  for (let i = 0; i < pallet.boxes.length; i++) {
    if (i === skipIdx) continue;
    const b = pallet.boxes[i];
    const overlapX = Math.min(x + dx, b.x + b.dx) - Math.max(x, b.x);
    const overlapY = Math.min(y + dy, b.y + b.dy) - Math.max(y, b.y);
    if (overlapX <= EPS || overlapY <= EPS) continue;
    if (b.can_support_load !== false) levels.add(Number((b.z + b.dz).toFixed(2)));
  }

  for (const z of Array.from(levels).sort((a, b) => a - b)) {
    if (!overlapsExisting(pallet, x, y, z, dx, dy, dz, skipIdx)) {
      return Number(z.toFixed(2));
    }
  }
  return null;
}

export function settleUnsupportedBoxes(pallet, pinnedIdx = -1) {
  // Cascade gravity downward after a support box moves/rotates/is removed.
  // Locked boxes and the actively manipulated box can be pinned in place.
  const maxPasses = Math.max(1, pallet.boxes.length + 1);
  for (let pass = 0; pass < maxPasses; pass++) {
    let changed = false;
    const order = pallet.boxes
      .map((b, i) => ({ i, z: b.z }))
      .sort((a, b) => a.z - b.z || a.i - b.i);

    for (const { i } of order) {
      if (i === pinnedIdx) continue;
      const b = pallet.boxes[i];
      if (!b || b.locked || b.z <= EPS) continue;
      const targetZ = findGravityZBelow(pallet, b.x, b.y, b.z, b.dx, b.dy, i);
      if (targetZ >= b.z - EPS) continue;
      if (overlapsExisting(pallet, b.x, b.y, targetZ, b.dx, b.dy, b.dz, i)) continue;
      pallet.boxes[i] = { ...b, z: targetZ };
      changed = true;
    }
    if (!changed) break;
  }
  return pallet;
}

export function getRotatedDimensions(box, axis) {
  let dx = box.dx, dy = box.dy, dz = box.dz;
  if (axis === "z") [dx, dy] = [dy, dx];
  else if (axis === "x") [dy, dz] = [dz, dy];
  else if (axis === "y") [dx, dz] = [dz, dx];
  return { dx, dy, dz };
}

export function centerAndClampXY(box, newDx, newDy, L, W, pallet = null) {
  const cx = box.x + box.dx / 2;
  const cy = box.y + box.dy / 2;
  const maxX = palletAxisLimit(pallet, "x", L);
  const maxY = palletAxisLimit(pallet, "y", W);
  const x = Math.max(0, Math.min(cx - newDx / 2, maxX - newDx));
  const y = Math.max(0, Math.min(cy - newDy / 2, maxY - newDy));
  return { x: Number(x.toFixed(2)), y: Number(y.toFixed(2)) };
}

// For a Y-axis rotation, prefer growing toward +X from the box's current left edge.
// If +X is blocked or outside the pallet, grow toward -X while keeping the old right edge.
export function findDirectionalYRotationPlacement(pallet, box, newDx, newDy, newDz, L, W, H, skipIdx = -1) {
  const candidates = [
    { x: box.x, y: box.y, direction: "+X" },
    { x: box.x + box.dx - newDx, y: box.y, direction: "-X" },
  ];
  const seen = new Set();
  for (const c of candidates) {
    const key = `${Number(c.x.toFixed(4))}|${Number(c.y.toFixed(4))}`;
    if (seen.has(key)) continue;
    seen.add(key);
    // Rotation is an in-layer operation. Do not interpret an XY overlap as
    // permission to climb onto another box; that behavior belongs to drag/drop.
    const z = Number((box.z || 0).toFixed(2));
    if (!fitsInPallet(pallet, c.x, c.y, z, newDx, newDy, newDz, L, W, H)) continue;
    if (overlapsExisting(pallet, c.x, c.y, z, newDx, newDy, newDz, skipIdx)) continue;
    return { x: Number(c.x.toFixed(2)), y: Number(c.y.toFixed(2)), z, direction: c.direction };
  }
  return null;
}

// X/Y nudge buttons move by STEP, but when less than STEP remains to the effective
// tolerance edge they snap exactly there instead of rejecting the move.
export function clampAxisNudge(box, axis, delta, L, W, H, pallet = null) {
  let x = box.x;
  let y = box.y;
  let z = box.z || 0;
  const maxX = palletAxisLimit(pallet, "x", L);
  const maxY = palletAxisLimit(pallet, "y", W);
  const maxZ = palletAxisLimit(pallet, "z", H);
  if (axis === "x") x = Math.max(0, Math.min(box.x + delta, maxX - box.dx));
  if (axis === "y") y = Math.max(0, Math.min(box.y + delta, maxY - box.dy));
  if (axis === "z") z = Math.max(0, Math.min((box.z || 0) + delta, maxZ - box.dz));
  return { x: Number(x.toFixed(2)), y: Number(y.toFixed(2)), z: Number(z.toFixed(2)) };
}

export function snapDragXY(pallet, idx, rawX, rawY, L, W, threshold = 2) {
  const box = pallet?.boxes?.[idx];
  if (!box) return { x: Number(rawX || 0), y: Number(rawY || 0) };
  const maxX = palletAxisLimit(pallet, "x", L);
  const maxY = palletAxisLimit(pallet, "y", W);
  const xMax = Math.max(0, maxX - box.dx);
  const yMax = Math.max(0, maxY - box.dy);
  const clamp = (v, max) => Math.max(0, Math.min(Number(v) || 0, max));
  let x = clamp(rawX, xMax), y = clamp(rawY, yMax);
  const xTargets = [0, xMax], yTargets = [0, yMax];
  (pallet.boxes || []).forEach((other, i) => {
    if (i === idx) return;
    xTargets.push(other.x - box.dx, other.x + other.dx);
    yTargets.push(other.y - box.dy, other.y + other.dy);
  });
  const nearest = (value, targets, max) => {
    let best = value, dist = threshold + EPS;
    for (const target of targets) {
      const candidate = clamp(target, max);
      const d = Math.abs(candidate - value);
      if (d < dist) { best = candidate; dist = d; }
    }
    return Number(best.toFixed(2));
  };
  return { x: nearest(x, xTargets, xMax), y: nearest(y, yTargets, yMax) };
}

export function findFreePosition(pallet, dx, dy, dz, L, W, H, skipIdx = -1) {
  const maxX = palletAxisLimit(pallet, "x", L);
  const maxY = palletAxisLimit(pallet, "y", W);
  const anchors = new Set(["0|0"]);
  pallet.boxes.forEach((b, i) => {
    if (i === skipIdx) return;
    if (b.x + b.dx <= maxX + EPS) anchors.add(`${b.x + b.dx}|${b.y}`);
    if (b.y + b.dy <= maxY + EPS) anchors.add(`${b.x}|${b.y + b.dy}`);
    if (b.x + b.dx <= maxX + EPS && b.y + b.dy <= maxY + EPS) anchors.add(`${b.x + b.dx}|${b.y + b.dy}`);
    anchors.add(`${b.x}|${b.y}`);
  });
  const cands = Array.from(anchors)
    .map((k) => { const [x, y] = k.split("|").map(Number); return { x, y }; })
    .sort((a, b) => a.y - b.y || a.x - b.x);
  for (const { x, y } of cands) {
    if (x + dx > maxX + EPS || y + dy > maxY + EPS) continue;
    const minZ = findRestingZ(pallet, x, y, dx, dy, dz, skipIdx);
    if (minZ == null) continue;
    if (fitsInPallet(pallet, x, y, minZ, dx, dy, dz, L, W, H) &&
        !overlapsExisting(pallet, x, y, minZ, dx, dy, dz, skipIdx)) {
      return { x, y, z: minZ };
    }
  }
  return null;
}
function recomputePalletMetrics(p) {
  const boxes = p.boxes;
  const usedVol = boxes.reduce((s, b) => s + b.dx * b.dy * b.dz, 0);
  const vol = p.L * p.W * p.H;
  const totalWeight = boxes.reduce((s, b) => s + (b.net_kg || 0), 0);
  const pkg = boxes.reduce((s, b) => s + (b.package_kg || 0), 0);
  const prod = boxes.reduce((s, b) => s + (b.product_kg || 0), 0);
  const topH = boxes.length ? Math.max(...boxes.map((b) => b.z + b.dz)) : 0;
  return {
    ...p,
    box_count: boxes.length,
    total_weight: Number(totalWeight.toFixed(3)),
    package_weight: Number(pkg.toFixed(3)),
    product_weight: Number(prod.toFixed(3)),
    top_height: Number(topH.toFixed(2)),
    fill_ratio: p.L && p.W && p.H
      ? Number((usedVol / (p.L * p.W * p.H)).toFixed(4))
      : (vol ? Number((usedVol / vol).toFixed(4)) : p.fill_ratio),
    empty_volume: p.L && p.W && p.H
      ? Number((p.L * p.W * p.H - usedVol).toFixed(2))
      : p.empty_volume,
  };
}

export default function PlanDetail() {
  const [qrVersion, setQrVersion] = useState(0);
  const { t } = useI18n();
  const { id } = useParams();
  const [plan, setPlan] = useState(null);
  const [activePallet, setActivePallet] = useState(0);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [hiddenLayerKeys, setHiddenLayerKeys] = useState([]);
  const [history, setHistory] = useState([]);
  const [addBoxSku, setAddBoxSku] = useState("");
  const [addBoxRotation, setAddBoxRotation] = useState("auto");
  const [palletTypes, setPalletTypes] = useState([]);
  const [palletModalOpen, setPalletModalOpen] = useState(false);
  const [multiSelIdxs, setMultiSelIdxs] = useState(new Set());
  const [planLock, setPlanLock] = useState({ locked: false, owned_by_me: false });
  const planLockTokenRef = useRef(null);
  const dragCandidate = useRef(null);
  const dragStartSnapshot = useRef(null);
  const readOnlyLocked = planLock.locked && !planLock.owned_by_me;
  const nav = useNavigate();
  const unitLabel = loadingUnitLabel(plan);
  const unitPlural = loadingUnitPlural(plan);
  const largeLoadingPlan = isLargeLoadingPlan(plan);

  const load = async () => {
    const r = await api.get(`/plans/${id}`);
    setPlan(r.data);
    const requestedPallet = new URLSearchParams(window.location.search).get("pallet");
    if (requestedPallet) {
      const index = (r.data.pallets || []).findIndex(p => String(p.pallet_no) === requestedPallet);
      if (index >= 0) setActivePallet(index);
    }
    setDirty(false);
    setHistory([]);
  };
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    let alive = true;
    let heartbeat = null;
    const acquire = async () => {
      try {
        const r = await api.post(`/plans/${id}/lock/acquire`);
        if (!alive) return;
        planLockTokenRef.current = r.data.token || null;
        setPlanLock(r.data);
        if (r.data.token) {
          heartbeat = window.setInterval(async () => {
            try {
              const hb = await api.post(`/plans/${id}/lock/heartbeat`, { token: planLockTokenRef.current });
              if (alive) setPlanLock((prev) => ({ ...prev, expires_at: hb.data.expires_at }));
            } catch (e) {
              if (!alive) return;
              planLockTokenRef.current = null;
              setPlanLock(e.response?.data?.detail?.lock || { locked: true, owned_by_me: false });
              toast.error("Plan düzenleme kilidi kaybedildi. Sayfa salt okunur moda geçti.");
            }
          }, 30000);
        }
      } catch (e) {
        if (!alive) return;
        const lock = e.response?.data?.detail?.lock;
        setPlanLock(lock || { locked: true, owned_by_me: false, user_name: "Başka bir kullanıcı" });
      }
    };
    acquire();
    return () => {
      alive = false;
      if (heartbeat) window.clearInterval(heartbeat);
      const token = planLockTokenRef.current;
      planLockTokenRef.current = null;
      if (token) api.post(`/plans/${id}/lock/release`, { token }).catch(() => {});
    };
  }, [id]);

  const pushHistory = (planState) => {
    setHistory((h) => {
      const snap = { pallets: JSON.parse(JSON.stringify(planState.pallets)), activePallet, selectedIdx };
      return [...h, snap].slice(-20);
    });
  };
  const pushHistorySnapshot = (snap) => {
    if (!snap) return;
    setHistory((h) => [...h, snap].slice(-20));
  };

  const undo = () => {
    if (readOnlyLocked) return toast.error(`Plan ${planLock.user_name || "başka bir kullanıcı"} tarafından düzenleniyor.`);
    setHistory((h) => {
      if (h.length === 0) { toast.info(t("final4b.detail.undo_none")); return h; }
      const last = h[h.length - 1];
      setPlan((prev) => ({ ...prev, pallets: last.pallets }));
      setActivePallet(last.activePallet);
      setSelectedIdx(last.selectedIdx);
      setDirty(true);
      return h.slice(0, -1);
    });
  };

  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const L = plan?.pallet_L, W = plan?.pallet_W, H = plan?.pallet_H;
  const currentPallet = useMemo(() => {
    if (!plan) return null;
    const raw = plan.pallets[activePallet];
    if (!raw) return null;
    const pL = raw.L || L, pW = raw.W || W, pH = raw.H || H;
    return { ...raw, L: pL, W: pW, H: pH };
  }, [plan, activePallet, L, W, H]);
  const selectedBox = selectedIdx != null && currentPallet ? currentPallet.boxes[selectedIdx] : null;

  useEffect(() => {
    api.get("/pallet-types").then((r) => setPalletTypes(r.data)).catch(() => {});
  }, []);

  if (!plan) return <div className="p-8 text-slate-500">{t("final4b.detail.loading")}</div>;

  const updateCurrentPallet = (mutator) => {
    if (readOnlyLocked) { toast.error(`Plan ${planLock.user_name || "başka bir kullanıcı"} tarafından düzenleniyor.`); return false; }
    const pallets = [...plan.pallets];
    const raw = pallets[activePallet];
    const pL = raw.L || L, pW = raw.W || W, pH = raw.H || H;
    const p = { ...raw, boxes: raw.boxes.map((b) => ({ ...b })) };
    const before = JSON.stringify(p);
    mutator(p);
    if (JSON.stringify(p) === before) return false;

    const geometry = validatePalletGeometry(p, pL, pW, pH);
    if (!geometry.valid) {
      toast.error(geometryErrorMessage(geometry));
      return false;
    }

    pushHistory(plan);
    const enriched = recomputePalletMetrics({ ...p, L: pL, W: pW, H: pH });
    const { L: _l, W: _w, H: _h, ...pClean } = enriched;
    if (raw.L) pClean.L = raw.L;
    if (raw.W) pClean.W = raw.W;
    if (raw.H) pClean.H = raw.H;
    pallets[activePallet] = pClean;
    setPlan({ ...plan, pallets });
    setDirty(true);
    return true;
  };

  const rotateSelected = (axis) => {
    if (selectedBox == null) return;
    updateCurrentPallet((p) => {
      const original = p.boxes[selectedIdx];
      const b = { ...original };
      const pL = p.L || L, pW = p.W || W, pH = p.H || H;
      const { dx, dy, dz } = getRotatedDimensions(b, axis);
      const prod = plan.products?.[b.sku];
      if (prod?.placement_rules?.allowed_rotations) {
        const targetRotation = ROTATIONS.find((r) => {
          const dims = r.fn(prod.length, prod.width, prod.height);
          return Math.abs(dims[0]-dx) < EPS && Math.abs(dims[1]-dy) < EPS && Math.abs(dims[2]-dz) < EPS;
        });
        if (targetRotation && !prod.placement_rules.allowed_rotations.includes(Number(targetRotation.key))) {
          toast.error("Bu ürün için bu rotasyona izin verilmiyor");
          return;
        }
      }

      if (!fitsInPallet(p, 0, 0, 0, dx, dy, dz, pL, pW, pH)) {
        toast.error(t("final4b.detail.rotation_not_fit"));
        return;
      }

      b.dx = dx; b.dy = dy; b.dz = dz;

      if (axis === "y") {
        const directed = findDirectionalYRotationPlacement(p, original, dx, dy, dz, pL, pW, pH, selectedIdx);
        if (!directed) {
          toast.error(t("final4b.detail.rotation_no_space"));
          return;
        }
        b.x = directed.x; b.y = directed.y; b.z = directed.z;
      } else {
        const centered = centerAndClampXY(original, dx, dy, pL, pW, p);
        b.x = centered.x; b.y = centered.y;
        b.z = findRestingZ(p, b.x, b.y, b.dx, b.dy, b.dz, selectedIdx);
        if (b.z == null || !fitsInPallet(p, b.x, b.y, b.z, b.dx, b.dy, b.dz, pL, pW, pH) ||
            overlapsExisting(p, b.x, b.y, b.z, b.dx, b.dy, b.dz, selectedIdx)) {
          const spot = findFreePosition(p, b.dx, b.dy, b.dz, pL, pW, pH, selectedIdx);
          if (!spot) { toast.error(t("final4b.detail.rotation_no_space")); return; }
          b.x = spot.x; b.y = spot.y; b.z = spot.z;
        }
      }

      p.boxes[selectedIdx] = b;
      settleUnsupportedBoxes(p);
    });
  };

  const nudge = (axis, delta) => {
    if (selectedBox == null) return;
    if (axis === "z" && delta < 0) {
      const p = currentPallet;
      const b = p.boxes[selectedIdx];
      const targetZ = findGravityZBelow(p, b.x, b.y, b.z, b.dx, b.dy, selectedIdx);
      if (Math.abs(targetZ - b.z) <= EPS) return;
      if (overlapsExisting(p, b.x, b.y, targetZ, b.dx, b.dy, b.dz, selectedIdx)) {
        toast.error(t("final4b.detail.collision"));
        return;
      }
      updateCurrentPallet((pl) => {
        pl.boxes[selectedIdx] = { ...pl.boxes[selectedIdx], z: targetZ };
      });
      return;
    }

    updateCurrentPallet((p) => {
      const b = { ...p.boxes[selectedIdx] };
      const pL = p.L || L, pW = p.W || W, pH = p.H || H;
      const snapped = clampAxisNudge(b, axis, delta, pL, pW, pH, p);
      const nx = axis === "x" ? snapped.x : b.x;
      const ny = axis === "y" ? snapped.y : b.y;
      const nz = axis === "z" ? snapped.z : findRestingZ(p, nx, ny, b.dx, b.dy, b.dz, selectedIdx);

      if (nz == null) { toast.error(t("final4b.detail.collision")); return; }
      if (Math.abs(nx - b.x) <= EPS && Math.abs(ny - b.y) <= EPS && Math.abs(nz - b.z) <= EPS) return;
      if (!fitsInPallet(p, nx, ny, nz, b.dx, b.dy, b.dz, pL, pW, pH)) {
        toast.error(t("final4b.detail.outside")); return;
      }
      if (overlapsExisting(p, nx, ny, nz, b.dx, b.dy, b.dz, selectedIdx)) {
        toast.error(t("final4b.detail.collision")); return;
      }
      b.x = Number(nx.toFixed(2));
      b.y = Number(ny.toFixed(2));
      b.z = Number(nz.toFixed(2));
      p.boxes[selectedIdx] = b;
      if (axis === "x" || axis === "y") settleUnsupportedBoxes(p, selectedIdx);
    });
  };

  const moveToPallet = (targetIdx) => {
    if (readOnlyLocked) return toast.error(`Plan ${planLock.user_name || "başka bir kullanıcı"} tarafından düzenleniyor.`);
    if (selectedBox == null || targetIdx === activePallet) return;
    const target = plan.pallets[targetIdx];
    const targetL = target.L || L, targetW = target.W || W, targetH = target.H || H;
    const targetEnriched = { ...target, L: targetL, W: targetW, H: targetH };
    const spot = findFreePosition(targetEnriched, selectedBox.dx, selectedBox.dy, selectedBox.dz, targetL, targetW, targetH);
    if (!spot) { toast.error(t("final4b.detail.target_not_fit")); return; }
    pushHistory(plan);
    const pallets = [...plan.pallets];
    const src = { ...pallets[activePallet], boxes: [...pallets[activePallet].boxes] };
    const removed = src.boxes.splice(selectedIdx, 1)[0];
    settleUnsupportedBoxes(src);
    const srcL = src.L || L, srcW = src.W || W, srcH = src.H || H;
    const srcMetrics = recomputePalletMetrics({ ...src, L: srcL, W: srcW, H: srcH });
    const { L: _sl, W: _sw, H: _sh, ...srcClean } = srcMetrics;
    if (src.L) srcClean.L = src.L; if (src.W) srcClean.W = src.W; if (src.H) srcClean.H = src.H;
    pallets[activePallet] = srcClean;
    const dst = { ...pallets[targetIdx], boxes: [...pallets[targetIdx].boxes] };
    dst.boxes.push({ ...removed, x: spot.x, y: spot.y, z: spot.z });
    const dstMetrics = recomputePalletMetrics({ ...dst, L: targetL, W: targetW, H: targetH });
    const { L: _dl, W: _dw, H: _dh, ...dstClean } = dstMetrics;
    if (dst.L) dstClean.L = dst.L; if (dst.W) dstClean.W = dst.W; if (dst.H) dstClean.H = dst.H;
    pallets[targetIdx] = dstClean;
    setPlan({ ...plan, pallets });
    setSelectedIdx(null);
    setDirty(true);
    toast.success(`${t("final4b.detail.box_moved")}: ${loadingUnitName(plan, target, targetIdx)}`);
  };

  const removeBox = () => {
    if (selectedBox == null) return;
    if (!window.confirm(t("final4b.detail.remove_box_confirm"))) return;
    updateCurrentPallet((p) => { p.boxes.splice(selectedIdx, 1); settleUnsupportedBoxes(p); });
    setSelectedIdx(null);
  };

  const toggleLock = () => {
    if (selectedBox == null) return;
    updateCurrentPallet((p) => {
      p.boxes[selectedIdx] = { ...p.boxes[selectedIdx], locked: !p.boxes[selectedIdx].locked };
    });
  };

  const removeEmptyPallet = (idx) => {
    if (readOnlyLocked) return toast.error(`Plan ${planLock.user_name || "başka bir kullanıcı"} tarafından düzenleniyor.`);
    const p = plan.pallets[idx];
    if (p.box_count > 0) return toast.error(`Sadece boş ${unitLabel.toLocaleLowerCase("tr-TR")} silinebilir.`);
    if (!window.confirm(`${loadingUnitName(plan, p, idx)} silinsin mi?`)) return;
    pushHistory(plan);
    const pallets = plan.pallets.filter((_, i) => i !== idx)
      .map((pl, i) => ({ ...pl, pallet_no: i + 1 }));
    setPlan({ ...plan, pallets });
    if (activePallet >= pallets.length) setActivePallet(Math.max(0, pallets.length - 1));
    setSelectedIdx(null);
    setDirty(true);
    toast.success(`${unitLabel} silindi: ${loadingUnitName(plan, p, idx)}`);
  };

  const addNewPallet = (pt) => {
    if (readOnlyLocked) return toast.error(`Plan ${planLock.user_name || "başka bir kullanıcı"} tarafından düzenleniyor.`);
    pushHistory(plan);
    const nextNo = plan.pallets.length + 1;
    const pL = pt ? pt.length : L;
    const pW = pt ? pt.width : W;
    const pH = pt ? pt.usable_height : H;
    const newPallet = {
      pallet_no: nextNo, boxes: [], box_count: 0, total_weight: 0,
      package_weight: 0, product_weight: 0, top_height: 0, fill_ratio: 0,
      empty_volume: pL * pW * pH,
      tolerance: Number(pt?.tolerance ?? 2),
      tolerance_x: !!pt?.tolerance_x,
      tolerance_y: !!pt?.tolerance_y,
      tolerance_z: pt?.tolerance_z !== false,
    };
    if (pt && (pt.length !== L || pt.width !== W || pt.usable_height !== H)) {
      newPallet.L = pL; newPallet.W = pW; newPallet.H = pH;
      newPallet.pallet_type_code = pt.code;
    } else if (pt) newPallet.pallet_type_code = pt.code;
    setPlan({ ...plan, pallets: [...plan.pallets, newPallet] });
    setActivePallet(plan.pallets.length);
    setSelectedIdx(null);
    setHiddenLayerKeys([]);
    setDirty(true);
    toast.success(`Yeni palet P${nextNo} eklendi${pt ? ` (${pt.code} ${pL}×${pW}×${pH})` : ""}`);
    setPalletModalOpen(false);
  };

  const handleAddLoadingUnit = () => {
    if (readOnlyLocked) return toast.error(`Plan ${planLock.user_name || "başka bir kullanıcı"} tarafından düzenleniyor.`);
    if (!largeLoadingPlan) {
      setPalletModalOpen(true);
      return;
    }
    const source = plan.pallets[0] || currentPallet;
    if (!source) return toast.error("Yükleme birimi bulunamadı.");
    pushHistory(plan);
    const nextNo = plan.pallets.length + 1;
    const pL = source.L || L;
    const pW = source.W || W;
    const pH = source.H || H;
    const newUnit = {
      ...source,
      pallet_no: nextNo,
      loading_unit_no: nextNo,
      boxes: [],
      box_count: 0,
      total_weight: 0,
      package_weight: 0,
      product_weight: 0,
      top_height: 0,
      fill_ratio: 0,
      empty_volume: pL * pW * pH,
    };
    setPlan({ ...plan, pallets: [...plan.pallets, newUnit] });
    setActivePallet(plan.pallets.length);
    setSelectedIdx(null);
    setHiddenLayerKeys([]);
    setDirty(true);
    toast.success(`Yeni ${unitLabel.toLocaleLowerCase("tr-TR")} ${nextNo} eklendi.`);
  };

  const ROTATIONS = [
    { key: "0", label: t("final4b.detail.orientation_default"), fn: (l, w, h) => [l, w, h] },
    { key: "1", label: t("final4b.detail.orientation_side"),  fn: (l, w, h) => [l, h, w] },
    { key: "2", label: t("final4b.detail.orientation_rotated"), fn: (l, w, h) => [w, l, h] },
    { key: "3", label: "G×Y×D",              fn: (l, w, h) => [w, h, l] },
    { key: "4", label: "Y×D×G (dik)",        fn: (l, w, h) => [h, l, w] },
    { key: "5", label: "Y×G×D",              fn: (l, w, h) => [h, w, l] },
  ];
  const addNewBox = () => {
    if (!addBoxSku) return toast.error(t("final4b.detail.select_product_error"));
    const products = plan.products || {};
    const prod = products[addBoxSku];
    if (!prod) return toast.error(t("final4b.detail.product_not_found"));
    const p = { ...currentPallet, boxes: [...currentPallet.boxes] };
    const pL = p.L, pW = p.W, pH = p.H;
    const allowedRotations = prod.placement_rules?.allowed_rotations || [0,1,2,3,4,5];
    const orientations = (addBoxRotation === "auto"
      ? ROTATIONS
      : [ROTATIONS.find((r) => r.key === addBoxRotation)].filter(Boolean))
      .filter((r) => allowedRotations.includes(Number(r.key)));
    if (!orientations.length) return toast.error("Bu ürün için seçilen rotasyona izin verilmiyor");
    let placed = null;
    let usedRot = null;
    for (const rot of orientations) {
      const [dx, dy, dz] = rot.fn(prod.length, prod.width, prod.height);
      const spot = findFreePosition(p, dx, dy, dz, pL, pW, pH);
      if (spot && !(prod.placement_rules?.base_only && spot.z > EPS)) { placed = { spot, dx, dy, dz }; usedRot = rot; break; }
    }
    if (!placed) {
      return toast.error(
        addBoxRotation === "auto"
          ? t("final4b.detail.no_rotation_fit")
          : `${orientations[0]?.label} ${t("final4b.detail.try_other")}`
      );
    }
    updateCurrentPallet((pl) => {
      pl.boxes.push({
        sku: prod.sku, inch: prod.inch, gtip: prod.gtip,
        description: prod.description || "",
        x: placed.spot.x, y: placed.spot.y, z: placed.spot.z,
        dx: placed.dx, dy: placed.dy, dz: placed.dz,
        net_kg: prod.net_kg, product_kg: prod.product_kg, package_kg: prod.package_kg,
        locked: false,
        can_support_load: prod.placement_rules?.can_support_load !== false,
        base_only: !!prod.placement_rules?.base_only,
        placement_rules: prod.placement_rules || {},
      });
    });
    toast.success(`${t("final4b.detail.box_added")}: ${prod.inch}" ${prod.sku} (${usedRot.label})`);
  };

  const onDropBox = ({ preview, idx, x, y, z }) => {
    if (readOnlyLocked) return;
    if (preview) {
      const p = plan.pallets[activePallet];
      const b = p.boxes[idx];
      if (!b || b.locked) return;
      const pL = p.L || L, pW = p.W || W, pH = p.H || H;
      const snapped = snapDragXY(p, idx, x, y, pL, pW);
      const nx = snapped.x;
      const ny = snapped.y;
      const nz = findRestingZ(p, nx, ny, b.dx, b.dy, b.dz, idx);
      if (nz == null) return;
      if (b.base_only && nz > EPS) return;
      if (!fitsInPallet(p, nx, ny, nz, b.dx, b.dy, b.dz, pL, pW, pH)) return;
      if (overlapsExisting(p, nx, ny, nz, b.dx, b.dy, b.dz, idx)) return;

      if (!dragStartSnapshot.current) {
        dragStartSnapshot.current = {
          pallets: JSON.parse(JSON.stringify(plan.pallets)), activePallet, selectedIdx,
        };
      }
      dragCandidate.current = { idx, x: nx, y: ny, z: nz };
      const pallets = [...plan.pallets];
      const cp = { ...pallets[activePallet], boxes: [...pallets[activePallet].boxes] };
      cp.boxes[idx] = { ...cp.boxes[idx], x: nx, y: ny, z: nz };
      pallets[activePallet] = cp;
      setPlan((prev) => ({ ...prev, pallets }));
      return;
    }

    if (dragCandidate.current) {
      const pallets = [...plan.pallets];
      const rawBase = pallets[activePallet];
      const raw = { ...rawBase, boxes: rawBase.boxes.map((b) => ({ ...b })) };
      settleUnsupportedBoxes(raw, dragCandidate.current.idx);
      const pL = raw.L || L, pW = raw.W || W, pH = raw.H || H;
      const geometry = validatePalletGeometry(raw, pL, pW, pH);
      if (!geometry.valid) {
        const fallback = dragStartSnapshot.current?.pallets || plan.pallets;
        setPlan((prev) => ({ ...prev, pallets: fallback }));
        toast.error(geometryErrorMessage(geometry));
        dragCandidate.current = null;
        dragStartSnapshot.current = null;
        return;
      }
      pushHistorySnapshot(dragStartSnapshot.current);
      setDirty(true);
      const cp = recomputePalletMetrics({ ...raw, L: pL, W: pW, H: pH });
      const { L: _l, W: _w, H: _h, ...pClean } = cp;
      if (raw.L) pClean.L = raw.L;
      if (raw.W) pClean.W = raw.W;
      if (raw.H) pClean.H = raw.H;
      pallets[activePallet] = pClean;
      setPlan((prev) => ({ ...prev, pallets }));
      setSelectedIdx(dragCandidate.current.idx);
      toast.success(t("final4b.detail.box_positioned"));
    }
    dragCandidate.current = null;
    dragStartSnapshot.current = null;
  };

  const duplicateSelectedBox = () => {
    if (readOnlyLocked) return toast.error(`Plan ${planLock.user_name || "başka bir kullanıcı"} tarafından düzenleniyor.`);
    if (!selectedBox) return toast.info("Önce çoğaltılacak kutuyu seçin.");
    const p = currentPallet;
    const pL = p.L || L, pW = p.W || W, pH = p.H || H;
    const spot = findFreePosition(p, selectedBox.dx, selectedBox.dy, selectedBox.dz, pL, pW, pH);
    if (!spot || (selectedBox.base_only && spot.z > EPS)) return toast.error("Aynı rotasyondaki kutu için boş yer bulunamadı.");
    const source = { ...selectedBox };
    updateCurrentPallet((pl) => {
      pl.boxes.push({ ...source, x: spot.x, y: spot.y, z: spot.z, locked: false });
    });
    toast.success(`${selectedBox.sku} aynı rotasyonla çoğaltıldı.`);
  };

  const goToRevise = async () => {
    if (readOnlyLocked) return toast.error(`Plan ${planLock.user_name || "başka bir kullanıcı"} tarafından düzenleniyor.`);
    if (dirty && !window.confirm("Kaydedilmemiş manuel değişiklikler var. Revize ekranına geçerseniz bu değişiklikler kaybolabilir. Devam edilsin mi?")) return;
    const token = planLockTokenRef.current;
    if (token) {
      try { await api.post(`/plans/${id}/lock/release`, { token }); } catch (_) {}
      planLockTokenRef.current = null;
    }
    nav(`/plans/${id}/edit`);
  };

  const boxSummary = (() => {
    if (!currentPallet) return [];
    const m = new Map();
    for (const b of currentPallet.boxes || []) {
      const key = `${b.inch}"`;
      m.set(key, (m.get(key) || 0) + 1);
    }
    return Array.from(m.entries()).sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]));
  })();

  const palletizedLoadContents = (() => {
    if (!currentPallet?.pallet_loads?.length) return [];
    return currentPallet.pallet_loads
      .map((load, index) => {
        const boxes = load?.source_pallet?.boxes || [];
        const skuCounts = new Map();
        boxes.forEach((box) => {
          const sku = box?.sku || "Bilinmeyen SKU";
          skuCounts.set(sku, (skuCounts.get(sku) || 0) + 1);
        });
        return {
          palletNo: load?.pallet_no || index + 1,
          boxCount: boxes.length,
          totalWeight: Number(load?.total_weight || 0),
          totalHeight: Number(load?.total_height || 0),
          stackLevel: Number(load?.z || 0) > EPS ? 2 : 1,
          position: { x: Number(load?.x || 0), y: Number(load?.y || 0), z: Number(load?.z || 0) },
          skuCounts: Array.from(skuCounts.entries()).sort((a, b) => a[0].localeCompare(b[0], "tr")),
          sourcePallet: load?.source_pallet || null,
        };
      })
      .sort((a, b) => a.palletNo - b.palletNo);
  })();

  const toggleMultiSel = (idx, e) => {
    if (e && (e.shiftKey || e.ctrlKey || e.metaKey)) {
      setMultiSelIdxs((prev) => {
        const next = new Set(prev);
        if (next.has(idx)) next.delete(idx); else next.add(idx);
        return next;
      });
    } else {
      setMultiSelIdxs(new Set());
      setSelectedIdx(idx);
    }
  };
  const clearMulti = () => setMultiSelIdxs(new Set());

  const handleAreaSelect = (indices, additive = false) => {
    setSelectedIdx(null);
    setMultiSelIdxs((prev) => {
      const next = additive ? new Set(prev) : new Set();
      for (const idx of indices || []) {
        if (additive && next.has(idx)) next.delete(idx); else next.add(idx);
      }
      return next;
    });
  };
  const bulkDelete = () => {
    if (multiSelIdxs.size === 0) return;
    if (!window.confirm(`${multiSelIdxs.size} kutu silinsin mi?`)) return;
    const count = multiSelIdxs.size;
    updateCurrentPallet((pl) => { pl.boxes = pl.boxes.filter((_, i) => !multiSelIdxs.has(i)); settleUnsupportedBoxes(pl); });
    setMultiSelIdxs(new Set());
    setSelectedIdx(null);
    toast.success(`${count} kutu silindi`);
  };
  const bulkRotate = () => {
    if (multiSelIdxs.size === 0) return;

    const pL = currentPallet.L || L;
    const pW = currentPallet.W || W;
    const pH = currentPallet.H || H;
    const selected = Array.from(multiSelIdxs)
      .map((i) => ({ i, box: currentPallet.boxes[i] }))
      .filter(({ box }) => box && !box.locked);

    if (selected.length === 0) {
      toast.info("Seçili kutular kilitli; döndürülecek kutu yok.");
      return;
    }

    for (const { box } of selected) {
      const { dx, dy, dz } = getRotatedDimensions(box, "z");
      if (!fitsInPallet(currentPallet, 0, 0, 0, dx, dy, dz, pL, pW, pH)) {
        toast.error(t("final4b.detail.rotation_not_fit"));
        return;
      }

      const prod = plan.products?.[box.sku];
      if (prod?.placement_rules?.allowed_rotations) {
        const targetRotation = ROTATIONS.find((r) => {
          const dims = r.fn(prod.length, prod.width, prod.height);
          return Math.abs(dims[0] - dx) < EPS && Math.abs(dims[1] - dy) < EPS && Math.abs(dims[2] - dz) < EPS;
        });
        if (targetRotation && !prod.placement_rules.allowed_rotations.includes(Number(targetRotation.key))) {
          toast.error("Seçili ürünlerden en az biri için Z rotasyonuna izin verilmiyor.");
          return;
        }
      }
    }

    const changed = updateCurrentPallet((pl) => {
      pl.boxes = pl.boxes.map((b, i) => {
        if (!multiSelIdxs.has(i) || b.locked) return b;
        const { dx: newDx, dy: newDy } = getRotatedDimensions(b, "z");
        const centered = centerAndClampXY(b, newDx, newDy, pL, pW, pl);
        return { ...b, dx: newDx, dy: newDy, x: centered.x, y: centered.y };
      });
      settleUnsupportedBoxes(pl);
    });

    if (changed) toast.success(`${selected.length} ${t("final4b.detail.boxes_rotated")}`);
  };

  const download = async (fmt) => {
    try {
      const token = localStorage.getItem("pallet_token");
      const res = await fetch(`${API}/plans/${id}/packing-list.${fmt}`, {
        credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(t("final4b.detail.download_failed"));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `packing_list_${plan.plan_no}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const approve = async () => {
    if (dirty) return toast.error(t("final4b.detail.save_first"));
    try {
      await api.post(`/plans/${id}/approve-recipe`);
      toast.success(t("final4b.detail.approved")); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveManualChanges = async () => {
    if (readOnlyLocked) return toast.error(`Plan ${planLock.user_name || "başka bir kullanıcı"} tarafından düzenleniyor.`);
    for (let i = 0; i < (plan.pallets || []).length; i += 1) {
      const candidate = plan.pallets[i];
      const geometry = validatePalletGeometry(candidate, candidate.L || L, candidate.W || W, candidate.H || H);
      if (!geometry.valid) {
        toast.error(`P${candidate.pallet_no || i + 1}: ${geometryErrorMessage(geometry)}`);
        return;
      }
    }
    try {
      await api.put(`/plans/${id}`, { pallets: plan.pallets }, { headers: { "X-Plan-Lock-Token": planLockTokenRef.current || "" } });
      toast.success(t("final4b.detail.manual_saved")); setDirty(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        title={`Plan ${plan.plan_no}`}
        subtitle={`${plan.shipment_name}${plan.consignee ? " · " + plan.consignee : ""} · ${plan.country} · ${plan.date}${plan.incoterms ? " · " + plan.incoterms : ""}`}>
        <button onClick={() => nav("/plans")} data-testid="btn-back-plans" className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-900 border border-slate-300 px-3 py-2 text-sm font-semibold rounded-sm"><ArrowLeft size={16} weight="bold" /> Planlara Dön</button>
        {dirty && <span className="text-xs text-amber-700 bg-amber-100 px-2 py-1 border border-amber-300" data-testid="dirty-flag">{t("plandetail.dirty")}</span>}
        {planLock.locked && <span data-testid="plan-lock-badge" className={`text-xs px-2 py-1 border inline-flex items-center gap-1 ${planLock.owned_by_me ? "text-emerald-700 bg-emerald-50 border-emerald-300" : "text-red-700 bg-red-50 border-red-300"}`}>{planLock.owned_by_me ? <LockOpen size={13} /> : <LockKey size={13} />}{planLock.owned_by_me ? "Düzenleme kilidi sende" : `${planLock.user_name || "Başka bir kullanıcı"} düzenliyor`}</span>}
        <button data-testid="btn-revise-plan" onClick={goToRevise} disabled={readOnlyLocked} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white px-3 py-2 text-sm font-semibold rounded-sm"><ArrowsClockwise size={16} weight="bold" /> Yeniden Revize</button>
        <button data-testid="btn-undo" onClick={undo} disabled={history.length === 0} title="Geri Al (Ctrl+Z)" className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-40 text-slate-700 px-3 py-2 text-sm font-semibold rounded-sm">
          <ArrowUUpLeft size={16} weight="bold" /> Geri Al {history.length > 0 && `(${history.length})`}
        </button>
        <button data-testid="btn-save-manual" onClick={saveManualChanges} disabled={!dirty || readOnlyLocked} className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 disabled:opacity-40 text-white px-3 py-2 text-sm font-semibold rounded-sm">
          <ArrowsClockwise size={16} weight="bold" /> {t("final4b.detail.save")}
        </button>
        <button data-testid="btn-approve" onClick={approve} disabled={readOnlyLocked} className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white px-3 py-2 text-sm font-semibold rounded-sm">
          <CheckCircle size={16} weight="bold" /> {t("plandetail.approve_recipe")}
        </button>
        {plan.status === "approved" && <button data-testid="btn-create-invoice" onClick={() => nav("/invoices")} className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-3 py-2 text-sm font-semibold rounded-sm"><Receipt size={16} weight="bold" /> {t("plandetail.create_invoice")}</button>}
        <button data-testid="btn-pdf" onClick={() => download("pdf")} className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-3 py-2 text-sm font-semibold rounded-sm"><FilePdf size={16} weight="bold" /> PDF</button>
        <button data-testid="btn-xlsx" onClick={() => download("xlsx")} className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-2 text-sm font-semibold rounded-sm"><FileXls size={16} weight="bold" /> Excel</button>
      </PageHeader>
      <DocumentDateEditor date={plan.date} path={`/plans/${id}`} label="Packing List Tarihi" disabled={dirty || readOnlyLocked} lockToken={planLockTokenRef.current} onSaved={async () => { await load(); setQrVersion(v => v + 1); }} />
      <PalletSharing plan={plan} dirty={dirty} locked={readOnlyLocked} lockToken={planLockTokenRef.current} onChange={() => setQrVersion(v => v + 1)} />

      <div className="p-4 sm:p-6 grid grid-cols-1 xl:grid-cols-[minmax(220px,0.8fr)_minmax(520px,2.2fr)_minmax(300px,1fr)] gap-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between px-1">
            <h3 className="font-display font-bold text-xs uppercase tracking-wider text-slate-500">{unitPlural} ({plan.pallets.length})</h3>
            <button onClick={handleAddLoadingUnit} data-testid="btn-add-pallet" className="text-xs text-[#2563EB] hover:text-[#1D4ED8] inline-flex items-center gap-1 font-semibold"><Plus size={12} weight="bold" /> Yeni {unitLabel}</button>
          </div>
          {plan.pallets.map((p, idx) => {
            const qrUrl = `${API}/plans/${plan.id}/pallets/${p.pallet_no}/qr.png?v=${qrVersion}`;
            const isEmpty = (p.box_count || 0) === 0;
            return (
              <div key={p.pallet_no} data-testid={`select-pallet-${p.pallet_no}`} onClick={() => { setActivePallet(idx); setSelectedIdx(null); setHiddenLayerKeys([]); setMultiSelIdxs(new Set()); }} className={`w-full text-left border p-3 flex gap-3 relative cursor-pointer ${activePallet === idx ? "border-[#2563EB] bg-blue-50" : "border-slate-200 bg-white hover:border-slate-400"}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-baseline"><div className="font-mono text-sm font-bold">{loadingUnitName(plan, p, idx)}{isEmpty && <span className="ml-1.5 text-[9px] px-1 py-0.5 bg-slate-100 text-slate-600 border border-slate-300 rounded-sm uppercase">{t("final4b.detail.empty")}</span>}</div><div className="text-[10px] font-mono text-slate-500">{p.box_count} kutu</div></div>
                  <div className="mt-1 h-1.5 bg-slate-200 relative"><div className="h-full bg-[#2563EB]" style={{ width: `${(p.fill_ratio * 100).toFixed(0)}%` }} /></div>
                  <div className="flex justify-between mt-1 text-[10px] font-mono text-slate-500"><span>%{(p.fill_ratio * 100).toFixed(1)}</span><span>{p.total_weight}kg</span><span>{p.top_height}cm</span></div>
                </div>
                <div className="flex-shrink-0"><img src={qrUrl} alt={`QR ${loadingUnitName(plan, p, idx)}`} className="w-11 h-11 bg-white border border-slate-200" data-testid={`qr-pallet-${p.pallet_no}`} /></div>
                {isEmpty && <button data-testid={`delete-empty-pallet-${p.pallet_no}`} onClick={(e) => { e.stopPropagation(); removeEmptyPallet(idx); }} title={t("final4b.detail.delete_empty")} className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-300 rounded-sm"><Trash size={12} /></button>}
              </div>
            );
          })}
        </div>

        <div className="flex flex-col gap-2 min-w-0">
          <div className="h-[560px] bg-white border border-slate-200 relative">
            {readOnlyLocked && <div data-testid="plan-lock-overlay" className="absolute inset-0 z-30 bg-slate-950/25 flex items-center justify-center pointer-events-auto"><div className="bg-white border border-red-300 shadow-lg px-4 py-3 text-sm text-red-800 font-semibold inline-flex items-center gap-2"><LockKey size={18} /> {planLock.user_name || "Başka bir kullanıcı"} bu planı düzenliyor · Salt okunur</div></div>}
            <Pallet3D pallet={currentPallet} L={currentPallet.L} W={currentPallet.W} H={currentPallet.H} products={plan.products || {}} onSelectBox={(b, i, ev) => toggleMultiSel(i, ev)} selectedIdx={selectedIdx} multiSelIdxs={Array.from(multiSelIdxs)} onAreaSelect={handleAreaSelect} hiddenLayerKeys={hiddenLayerKeys} onDropBox={onDropBox} />
          </div>
          <div className="flex justify-between text-xs text-slate-500"><span>{t("final4b.detail.mouse_help")}</span><span className="font-mono">{unitLabel}: {currentPallet.L}×{currentPallet.W}×{currentPallet.H} cm{currentPallet.pallet_type_code ? ` · ${currentPallet.pallet_type_code}` : ""}</span></div>

          {boxSummary.length > 0 && <div className="bg-white border border-slate-200 px-3 py-3" data-testid="box-summary">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{t("final4b.detail.summary")}</div>
              {plan.status === "approved" && <button type="button" data-testid="btn-loading-simulation-inline" onClick={() => window.dispatchEvent(new Event("h3poe:open-loading-simulation"))} className="inline-flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white px-3 py-2 text-xs font-bold shadow-sm rounded-sm"><Package size={15} weight="fill" /> Yükleme Simülasyonu</button>}
            </div>
            <div className="flex flex-wrap gap-2">{boxSummary.map(([label, count]) => <span key={label} data-testid={`summary-${label.replace(/[^0-9]/g, "")}`} className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-300 px-2.5 py-1 rounded-sm text-sm font-mono"><b className="text-slate-900 text-[14px]">{label}</b><span className="text-slate-500">×</span><span className="text-[#2563EB] font-extrabold text-[15px]">{count}</span></span>)}</div>
          </div>}

          <NestedPalletEditor
            planId={id}
            loads={palletizedLoadContents}
            products={plan.products || {}}
            unitLabel={unitLabel}
            readOnly={readOnlyLocked}
            lockToken={planLockTokenRef.current || ""}
            onUpdated={load}
          />
        </div>

        <div className="space-y-4 min-w-0">
          <div className="bg-white border border-slate-200 p-4"><div className="text-[10px] uppercase tracking-wider text-slate-500">{unitLabel} Metrikleri</div><div className="grid grid-cols-2 gap-2 mt-2 text-sm"><Stat label={`${unitLabel} No`} value={loadingUnitName(plan, currentPallet, activePallet)} /><Stat label={t("final4b.detail.box")} value={currentPallet.box_count} /><Stat label="Doluluk" value={`${(currentPallet.fill_ratio * 100).toFixed(1)}%`} /><Stat label={t("final4b.detail.height")} value={`${currentPallet.top_height} cm`} /><Stat label={t("final4b.detail.weight")} value={`${currentPallet.total_weight} kg`} /><Stat label={t("final4b.detail.empty_volume")} value={`${(currentPallet.empty_volume / 1000).toFixed(1)} L`} /></div></div>

          {multiSelIdxs.size > 0 && <div className="sticky top-2 z-20 flex flex-wrap items-center gap-2 bg-amber-50 border border-amber-300 px-3 py-3 shadow-sm rounded-sm" data-testid="multi-sel-toolbar">
            <span className="w-full text-xs font-semibold text-amber-900" data-testid="multi-sel-count">{multiSelIdxs.size} {t("final4b.detail.boxes_selected")}</span>
            <button data-testid="btn-bulk-rotate" onClick={bulkRotate} className="text-xs bg-slate-800 hover:bg-slate-900 text-white px-2.5 py-1.5 font-semibold rounded-sm inline-flex items-center gap-1"><ArrowsClockwise size={12} weight="bold" /> {t("final4b.detail.bulk_rotate")}</button>
            <button data-testid="btn-bulk-delete" onClick={bulkDelete} className="text-xs bg-red-600 hover:bg-red-700 text-white px-2.5 py-1.5 font-semibold rounded-sm inline-flex items-center gap-1"><Trash size={12} weight="bold" /> {t("final4b.detail.bulk_delete")}</button>
            <button data-testid="btn-clear-multi" onClick={clearMulti} className="text-xs bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-2.5 py-1.5 font-semibold rounded-sm">{t("final4b.detail.clear_selection")}</button>
          </div>}

          {selectedBox ? <div className="bg-[#0F172A] text-white p-4 space-y-3" data-testid="manual-edit-panel">
            <div className="flex justify-between items-start"><div><div className="text-[10px] uppercase tracking-wider text-slate-400">{t("final4b.detail.selected_box")} #{selectedIdx + 1}</div><div className="font-display text-xl font-bold mt-0.5">{selectedBox.inch}" · {selectedBox.sku}</div></div><button onClick={toggleLock} data-testid="btn-lock" className={`text-xs px-2 py-1 border ${selectedBox.locked ? "bg-yellow-500 text-slate-900 border-yellow-400" : "border-slate-600 text-slate-300 hover:bg-slate-700"}`}>{selectedBox.locked ? <span className="inline-flex items-center gap-1"><LockKey size={12} weight="fill" /> Kilitli</span> : <span className="inline-flex items-center gap-1"><LockOpen size={12} /> Kilit</span>}</button></div>
            <div className="text-[11px] font-mono text-slate-300 space-y-0.5 border-t border-slate-800 pt-2"><div>Konum: <b>({selectedBox.x}, {selectedBox.y}, {selectedBox.z})</b> cm</div><div>Boyut: <b>{selectedBox.dx}×{selectedBox.dy}×{selectedBox.dz}</b> cm</div><div>{t("final4b.detail.weight")}: <b>{selectedBox.net_kg}</b> kg</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{t("final4b.detail.rotate")}</div><div className="grid grid-cols-3 gap-1"><button onClick={() => rotateSelected("x")} data-testid="rot-x" className="bg-slate-800 hover:bg-slate-700 py-1.5 text-xs font-mono flex flex-col items-center border border-slate-700"><ArrowsClockwise size={14} /><span>X ekseni</span></button><button onClick={() => rotateSelected("y")} data-testid="rot-y" className="bg-slate-800 hover:bg-slate-700 py-1.5 text-xs font-mono flex flex-col items-center border border-slate-700"><ArrowsClockwise size={14} /><span>Y ekseni</span></button><button onClick={() => rotateSelected("z")} data-testid="rot-z" className="bg-slate-800 hover:bg-slate-700 py-1.5 text-xs font-mono flex flex-col items-center border border-slate-700"><ArrowsClockwise size={14} /><span>Z ekseni</span></button></div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{t("final4b.detail.move_position")} · X/Y ±{STEP}cm · Z- gravity</div><div className="grid grid-cols-6 gap-1"><NudgeBtn label="X-" onClick={() => nudge("x", -STEP)} icon={ArrowLeft} testid="nudge-x-minus" /><NudgeBtn label="X+" onClick={() => nudge("x", STEP)} icon={ArrowRight} testid="nudge-x-plus" /><NudgeBtn label="Y-" onClick={() => nudge("y", -STEP)} icon={ArrowLeft} testid="nudge-y-minus" /><NudgeBtn label="Y+" onClick={() => nudge("y", STEP)} icon={ArrowRight} testid="nudge-y-plus" /><NudgeBtn label="Z-" onClick={() => nudge("z", -STEP)} icon={ArrowFatDown} testid="nudge-z-minus" /><NudgeBtn label="Z+" onClick={() => nudge("z", STEP)} icon={ArrowFatUp} testid="nudge-z-plus" /></div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{unitLabel} Değiştir</div><select data-testid="move-to-pallet" className="w-full bg-slate-800 border border-slate-700 text-white text-xs px-2 py-1.5 font-mono" onChange={(e) => { const v = e.target.value; if (v) moveToPallet(Number(v)); e.target.value = ""; }}><option value="">{t("final4b.detail.select_target")}</option>{plan.pallets.map((p, i) => i !== activePallet && <option key={p.pallet_no} value={i}>{loadingUnitName(plan, p, i)}</option>)}</select></div>
            <button onClick={duplicateSelectedBox} data-testid="duplicate-box" className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white text-xs py-1.5 font-semibold inline-flex items-center justify-center gap-1"><Plus size={12} /> Aynısını Çoğalt</button>
            <button onClick={removeBox} data-testid="remove-box" className="w-full mt-2 bg-red-600 hover:bg-red-700 text-white text-xs py-1.5 font-semibold inline-flex items-center justify-center gap-1"><Trash size={12} /> {t("final4b.detail.delete_box")}</button>
          </div> : <div className="bg-slate-100 border border-slate-200 p-4 text-center text-xs text-slate-500">{t("final4b.detail.select_box_manual")}</div>}

          <LayerPanel pallet={currentPallet} L={currentPallet.L} W={currentPallet.W} hiddenLayerKeys={hiddenLayerKeys} setHiddenLayerKeys={setHiddenLayerKeys} selectedIdx={selectedIdx} onSelectBox={(b, i) => setSelectedIdx(i)} />

          <div className="bg-white border border-slate-200 p-4" data-testid="add-box-panel">
            <div className="flex items-center gap-2 mb-2"><Package size={14} weight="duotone" className="text-[#2563EB]" /><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("final4b.detail.add_box")}</div></div>
            <div className="flex gap-2 mb-2 min-w-0 max-w-full overflow-hidden"><select value={addBoxSku} onChange={(e) => setAddBoxSku(e.target.value)} data-testid="add-box-sku" className="flex-1 min-w-0 w-0 max-w-full border border-slate-300 focus:border-[#2563EB] outline-none px-2 py-1.5 text-sm rounded-sm bg-white"><option value="">{t("plandetail.select_product")}</option>{Object.values(plan.products || {}).map((p) => {
  const label = (p.attributes?.inch || p.inch)
    ? `${p.attributes?.inch || p.inch}" · ${p.sku}`
    : (p.name ? `${p.name} · ${p.sku}` : p.sku);
  return <option key={p.sku} value={p.sku}>{label} ({p.length}×{p.width}×{p.height})</option>;
})}</select><button onClick={addNewBox} data-testid="btn-add-box" disabled={!addBoxSku} className="shrink-0 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-40 text-white px-3 text-sm font-semibold inline-flex items-center justify-center gap-1 rounded-sm"><Plus size={14} weight="bold" /></button></div>
            <label className="block"><div className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">{t("final4b.detail.orientation")}</div><select value={addBoxRotation} onChange={(e) => setAddBoxRotation(e.target.value)} data-testid="add-box-rotation" className="w-full border border-slate-300 focus:border-[#2563EB] outline-none px-2 py-1 text-xs rounded-sm bg-white"><option value="auto">Otomatik (6 rotasyon dene)</option>{ROTATIONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}</select></label>
            <div className="text-[10px] text-slate-500 mt-1">{t("final4b.detail.auto_help")}</div>
          </div>

          <div className="bg-white border border-slate-200 p-4"><div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">{t("final4b.detail.box_list")}</div><div className="max-h-64 overflow-y-auto text-xs">{currentPallet.boxes.map((b, i) => <div key={i} data-testid={`box-item-${i}`} className={`flex justify-between items-center py-1.5 px-2 border-b border-slate-100 cursor-pointer ${selectedIdx === i ? "bg-blue-50 border-l-2 border-l-[#2563EB]" : "hover:bg-slate-50"}`} onClick={() => setSelectedIdx(i)}><div className="font-mono"><span className="font-bold">{b.inch}"</span> <span className="text-slate-500">{b.sku}</span></div><div className="flex items-center gap-2 text-[10px] font-mono text-slate-400"><span>({b.x},{b.y},{b.z})</span>{b.locked && <LockKey size={12} weight="fill" className="text-yellow-600" />}</div></div>)}</div></div>
        </div>
      </div>

      {palletModalOpen && <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPalletModalOpen(false)} data-testid="pallet-modal"><div className="bg-white w-full max-w-lg border border-slate-300" onClick={(e) => e.stopPropagation()}><div className="flex items-center justify-between border-b border-slate-200 px-5 py-3"><h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-700">{t("final4b.detail.add_pallet")}</h3><button onClick={() => setPalletModalOpen(false)} className="text-slate-500 hover:text-slate-900" data-testid="pallet-modal-close">✕</button></div><div className="p-5 space-y-2 max-h-[60vh] overflow-y-auto"><div className="text-xs text-slate-500 mb-2">{t("final4b.detail.select_pallet_type")}</div>{palletTypes.map((pt) => { const isPlanDefault = pt.length === L && pt.width === W && pt.usable_height === H; return <button key={pt.id} data-testid={`pt-option-${pt.code}`} onClick={() => addNewPallet(pt)} className={`w-full text-left border p-3 flex items-center justify-between hover:border-[#2563EB] hover:bg-blue-50 ${isPlanDefault ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`}><div><div className="font-display font-bold text-sm">{pt.name} <span className="text-xs text-slate-500 font-mono ml-1">{pt.code}</span>{isPlanDefault && <span className="ml-2 text-[9px] px-1.5 py-0.5 bg-emerald-600 text-white uppercase rounded-sm">{t("final4b.detail.plan_default")}</span>}</div><div className="text-[11px] font-mono text-slate-500 mt-0.5">{pt.length}×{pt.width}×{pt.total_height} cm · {t("final4b.detail.usable")} {pt.usable_height}cm</div></div><Plus size={16} weight="bold" className="text-[#2563EB]" /></button>; })}{palletTypes.length === 0 && <div className="text-center text-slate-400 py-6 text-sm">{t("final4b.detail.no_pallet_type")}</div>}</div></div></div>}
    </div>
  );
}

function Stat({ label, value }) {
  return <div><div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div><div className="font-display font-bold text-lg data-cell">{value}</div></div>;
}

function NudgeBtn({ label, onClick, icon: Icon, testid }) {
  return <button onClick={onClick} data-testid={testid} className="bg-slate-800 hover:bg-slate-700 py-1.5 text-[10px] font-mono border border-slate-700 flex flex-col items-center"><Icon size={12} /><span>{label}</span></button>;
}
